import intl from "react-intl-universal";
// Cropped from the customer-supplied TriangleWarning.jpg (ticket attachment), background
// removed and downscaled (service-desk#1617).
import warningTriangle from "../../asset/ixus/warningTriangle.png";

// Marks a placed text field as the safety distance table instead of a plain warning text.
const kind = "safetyDistances";
// Kinds of the other placeable report symbols (service-desk#1617), stored the same way.
const cloudKind = "cloud";
const cloudOpacity = 0.5;
const directionalRadioKind = "directionalRadio";
const climbingSquareKind = "climbingSquare";
// The arrow is user-drawn (start/end point), not a fixed icon (service-desk#1668).
const arrowKind = "arrow";
// Solid red line (service-desk#1694), drawn the same way as the arrow.
const redLineKind = "redLine";

// Climbing squares are drawn true to scale, 80 cm edge length (MockUp #1617, slide 1).
const climbingSquareSizeMeters = 0.8;

// The warning triangle sits above every safety distance table, except on these site types -
// mirrors the PYLON_DFMG_GT_60M / MAJOR_SITE cases in IxusReportService.java.
const siteTypesWithoutWarningTriangle = ["PYLON_DFMG_GT_60M", "MAJOR_SITE"];

const directions = Object.freeze({
    FRONT: "FRONT",
    TOP_BOTTOM: "TOP_BOTTOM",
    SIDES: "SIDES",
    BACK: "BACK",
});

// Safety distances are rounded up to half meter steps (2.92 m -> 3.00 m).
const step = 0.5;

const numberFormat = new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
});

// Measurements used to be stored as a bare point pair, before they carried a direction.
const toMeasurement = measurement => (Array.isArray(measurement)
    ? { points: measurement, direction: null, levelPlanKey: null }
    : measurement);

const getLength = points => {
    const [p1, p2] = points;
    return Math.hypot(p2.x - p1.x, p2.y - p1.y);
};

const formatDistance = meters => (meters === 0 ? "0 m" : numberFormat.format(meters) + " m");

// A level is a top view; its distances are collected from the measurements of all views,
// because "oben, unten" is measured on a side view but belongs to a top view.
const getSafetyDistances = (levelPlanKey, ixusAreas) => {
    const distances = { FRONT: 0, TOP_BOTTOM: 0, SIDES: 0, BACK: 0 };
    if (levelPlanKey === null || levelPlanKey === undefined) {
        return distances;
    }
    for (const ixusArea of ixusAreas) {
        const measurements = ixusArea.calculation?.ixusResult?.measurements || [];
        for (const measurement of measurements) {
            const { points, direction, levelPlanKey: planKey } = toMeasurement(measurement);
            if (planKey !== levelPlanKey || distances[direction] === undefined) {
                continue;
            }
            distances[direction] = Math.max(distances[direction], Math.ceil(getLength(points) / step) * step);
        }
    }
    return distances;
};

// Levels without a plan are dropped implicitly, so measurements and text boxes that point
// at a deleted plan are ignored everywhere the level list is used.
const getLevels = (ixusAreas, plans) => plans
    .map((plan, index) => ({ plan, index }))
    .filter(({ plan }) => ixusAreas.some(ixusArea => ixusArea.planKey === plan.key && !ixusArea.sideView))
    .map(({ plan, index }) => ({
        planKey: plan.key,
        label: plan.layerName || intl.get("entry.ixus.measurement.defaultLevel", { number: index + 1 }),
    }));

const buildContent = (levelPlanKey, ixusAreas, note, siteType) => {
    const distances = getSafetyDistances(levelPlanKey, ixusAreas);
    return {
        title: intl.get("entry.ixus.safetyDistances.title"),
        rows: [
            [intl.get("entry.ixus.safetyDistances.front"), formatDistance(distances.FRONT)],
            [intl.get("entry.ixus.safetyDistances.topBottom"), formatDistance(distances.TOP_BOTTOM)],
            [intl.get("entry.ixus.safetyDistances.sides"), formatDistance(distances.SIDES)],
            [intl.get("entry.ixus.safetyDistances.back"), formatDistance(distances.BACK)],
        ],
        footer: note,
        headerImage: siteTypesWithoutWarningTriangle.includes(siteType) ? undefined : warningTriangle,
    };
};

const safetyDistanceUtils = {
    kind,
    cloudKind,
    cloudOpacity,
    directionalRadioKind,
    climbingSquareKind,
    arrowKind,
    redLineKind,
    climbingSquareSizeMeters,
    directions,
    toMeasurement,
    formatDistance,
    getSafetyDistances,
    getLevels,
    buildContent,
};
export default safetyDistanceUtils;
